import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import Sites from '@toasttab/shared/src/data/Sites.json'
// eslint-disable-next-line no-restricted-imports
import { ReactComponent as CloseIcon } from '@toasttab/shared/src/assets/svg/icon-close.svg'
import { useI18next } from '@toasttab/shared/src/hooks/useI18next'
import Link from '@toasttab/shared/src/components/atoms/Link/Link'

export interface RegionSelectorProps {
  currentLanguage?: string
  footerLinkCss: any
  localizedEntries?: any[]
  packageName: string
  siteId: number
}

const getLinkLanguage = (language: string) => {
  switch (language) {
    case 'en-US':
      return 'en'
    case 'en-GB':
      return 'uk'
    case null:
      return null
    case undefined:
      return undefined
    default:
      return language.toLowerCase().split('-')[1]
  }
}

const RegionSelector = ({
  currentLanguage,
  footerLinkCss,
  localizedEntries,
  siteId
}: RegionSelectorProps) => {
  const { languages, originalPath, localized, ready } = useI18next()
  const [showRegionModal, setRegionModal] = useState(false)

  const modalClasses = classNames(
    'fixed bg-black bg-opacity-30 transition-opacity inset-0 left-0 top-0 w-full z-50 flex min-h-full items-center justify-center p-4 text-center',
    {
      hidden: !showRegionModal,
      flex: showRegionModal
    }
  )

  const getCurrentSiteName = () => {
    const site = Sites.sites.find((x) => x.siteId === siteId)
    if (!site) return
    return site?.siteName
  }

  return (
    <>
      <div>
        <button
          aria-label='Open region selector'
          className={`${footerLinkCss} before:content-icon-map-marker before:mr-2 before:relative before:top-1`}
          data-modal-target='regionSelect-modal'
          data-modal-toggle='regionSelect-modal'
          data-testid='RegionSelector-openButton'
          onClick={() => setRegionModal(!showRegionModal)}
        >
          Region: {getCurrentSiteName()}
        </button>
      </div>
      <div
        aria-hidden={showRegionModal}
        aria-modal={showRegionModal}
        className={modalClasses}
        data-testid='RegionSelector-modal'
        id='regionSelect-modal'
        onClick={() => setRegionModal(false)}
        role={showRegionModal ? 'none' : 'modal'}
      >
        <div className='relative bg-white rounded-lg shadow-xl max-w-[550px] max-h-[800px] w-full overflow-y-auto'>
          <button
            aria-label='Close region selector'
            className='absolute top-1 right-1'
            onClick={() => setRegionModal(false)}
          >
            <CloseIcon width='30' height='30' />
          </button>
          <nav aria-label='Switch language' role='navigation' className='p-10'>
            <h4
              className='relative text-default font-effra text-2xl font-medium mb-6'
              id='modal-title'
            >
              Select your region
            </h4>
            <ul>
              <>
                {ready &&
                  localized &&
                  languages.map((lng) => {
                    const site = Sites.sites
                      .filter((site) => site.countryCode !== 'AU')
                      .filter((site) => site.defaultRegion)
                      .find((x) => x.regionCode === lng)
                    if (!site) return null
                    return (
                      <li key={lng} data-testid='RegionSelector-listItem'>
                        <Link
                          to={originalPath}
                          language={lng}
                          lang={site.language}
                        >
                          {site.siteName}
                        </Link>
                      </li>
                    )
                  })}
                {ready &&
                  !localized &&
                  languages.map((lng) => {
                    const site = Sites.sites
                      .filter((site) => site.countryCode !== 'AU')
                      .filter((site) => site.defaultRegion)
                      .find((x) => x.regionCode === lng)
                    if (!site) return null
                    return (
                      <li
                        key={site.regionCode}
                        data-testid='RegionSelector-listItem'
                      >
                        <a
                          href={`${site.url === '' ? originalPath : site.url}`}
                        >
                          {site.siteName}
                        </a>
                      </li>
                    )
                  })}
                {!ready &&
                  Sites.sites
                    .filter((site) => site.defaultRegion)
                    .map((site) => {
                      return (
                        <li
                          key={site.regionCode}
                          data-testid='RegionSelector-listItem'
                        >
                          <a href={`${site.url === '' ? '/' : site.url}`}>
                            {site.siteName}
                          </a>
                        </li>
                      )
                    })}
              </>
            </ul>
          </nav>
        </div>
      </div>
    </>
  )
}

export default RegionSelector
